import type { ParticipantHealth } from "@teamna/shared";
import { useParticipantHealth } from "@teamna/web-client/hooks/use-health.ts";
import { formatDate } from "@teamna/web-client/lib/format.ts";
import { Badge, HealthNoteBadge, SkeletonList } from "../../components/index.ts";

/**
 * Health on the client page and on each child's card (C2). The owner and the admins read the
 * answers, the notes and the version of the terms the person accepted; a reader the server does not
 * allow gets the flag only (the server sends no details). Nothing when the person never answered.
 *
 * `HealthDetailsView` takes plain props, so Storybook draws it.
 */
export function HealthDetails({ businessId, participantId, timezone }: { businessId: string; participantId: string; timezone: string }) {
  const health = useParticipantHealth(businessId, participantId);
  if (health.isLoading) return <SkeletonList rows={1} />;
  if (!health.data) return null;
  return <HealthDetailsView health={health.data} timezone={timezone} />;
}

export function HealthDetailsView({ health, timezone }: { health: ParticipantHealth; timezone: string }) {
  if (!health.answered) return null;
  return (
    <section className="flex flex-col gap-2" aria-label="Health">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[16px]">Health</h3>
        {health.hasFlag ? <HealthNoteBadge /> : <Badge tone="confirmed">No health note</Badge>}
      </div>
      {health.details ? (
        <>
          <ul className="divide-y divide-line rounded-card border border-line" aria-label="Health answers">
            {health.details.answers.map((answer) => (
              <li key={answer.questionId} className="flex flex-col gap-0.5 px-4 py-2.5">
                <span className="flex items-start justify-between gap-3">
                  <span className="text-[14px] text-ink" dir="auto">
                    {answer.question}
                  </span>
                  <span className={`shrink-0 text-[14px] font-semibold ${answer.yes ? "text-late-ink" : "text-ink-2"}`}>{answer.yes ? "Yes" : "No"}</span>
                </span>
                {answer.note ? (
                  <span className="text-[13px] whitespace-pre-line text-ink-2" dir="auto">
                    {answer.note}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="text-[13px] text-ink-2">
            Answered on {formatDate(health.details.answeredAt, timezone)}.
            {health.details.waiver
              ? ` Accepted the terms, version ${health.details.waiver.version}, on ${formatDate(health.details.waiver.acceptedAt, timezone)}.`
              : " No terms accepted."}
          </p>
        </>
      ) : health.hasFlag ? (
        <p className="text-[13px] text-ink-2">The owner and the admins read the answers. Ask them before the session.</p>
      ) : null}
    </section>
  );
}
